import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { B2CDomainSelector } from '@yola/webapp-plugins';
import { site } from '@yola/ws-sdk';
import {
  selectors as subscriptionSelectors,
  verifiers as subscriptionManagerVerifiers,
} from '@yola/subscription-manager-js';
import segment from 'src/js/modules/analytics/segment';
import common from 'src/js/modules/common';
import getPublishUrl from 'src/js/modules/editor/helpers/get-publish-url';
import config from 'src/js/modules/config';
import dialogs from 'yola-editor/src/js/modules/dialogs';
import actions from 'yola-editor/src/js/modules/upsell/actions';
import getUserPreferences from 'yola-editor/src/js/modules/user/selectors/user-preferences';
import getSiteId from 'yola-editor/src/js/modules/auth/selectors/site-id';
import getSiteData from 'yola-editor/src/js/modules/site/selectors/site-data';
import convertStringToDomainName from 'yola-editor/src/js/modules/upsell/helpers/convert-string-to-domain-name';
import useDomainsExtraPrice, {
  extraPrices,
} from 'yola-editor/src/js/modules/upsell/hooks/use-domains-extra-price';
import { PUBLISHING } from 'yola-editor/src/js/modules/upsell/constants/upsell-types';
import publishing from 'yola-editor/src/js/modules/publishing';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import actionIds from '../constants/domain-purchase-action-ids';
import usePurchasedDomainsData from '../hooks/use-purchased-domains-data';
import isSpecialOfferAvailable from '../helpers/is-special-offer-available';

const {
  constants,
  trackers: { trackEvent },
} = segment;

const defaultCaptionOverrides = {};
const DEFAULT_TLD = 'com';

function B2cDomainPurchaseContainer({
  view = 'dialog',
  triggerId,
  domainNameSuggestion = '',
  withFreeSubdomain = false,
  withPurchasedDomains = false,
  shouldPublishOnSuccess = false,
  captionsOverrides = defaultCaptionOverrides,
  premiumFeature = '',
  onClose = null,
  onNext = null,
  onSelect,
}) {
  const dispatch = useDispatch();
  const { currency } = useSelector(getUserPreferences);
  const siteId = useSelector(getSiteId);
  const siteData = useSelector(getSiteData, shallowEqual);
  const { name: siteName, locale: siteLocale } = siteData;
  const siteSettings = useSelector(site.selectors.getSettings);
  const packageType = useSelector(subscriptionSelectors.getCurrentPackageType);
  const activeHostingSubscription = useSelector(subscriptionSelectors.getActiveHostingSubscription);
  const myYolaConfig = useSelector(config.selectors.getMyYolaConfig);

  const [featureFlags] = useFeatureFlags(['upgrade_to_publish']);

  const { upgrade_to_publish: isUpgradeToPublishEnabled } = featureFlags;

  const isActiveTrialSubscription =
    activeHostingSubscription &&
    subscriptionManagerVerifiers.isTrialSubscription(activeHostingSubscription);

  const extraPriceType = useDomainsExtraPrice();
  const purchasedDomainsData = usePurchasedDomainsData(withPurchasedDomains);

  const { businessName, businessCategory, businessDescription } = siteSettings;
  const isTrialDiscountAvailable =
    extraPriceType === extraPrices.ANNUAL_TRIAL_PACKAGE ||
    (isActiveTrialSubscription && extraPriceType === extraPrices.NO_DISCOUNTS);

  const trackDomainPurchaseClicked = ({ domain, searchString, actionId }) => {
    const indexOfDomainTypeSeparator = domain.indexOf('.');
    const domainNameSelected = domain.substring(0, indexOfDomainTypeSeparator);
    const domainTypeSelected = domain.substring(indexOfDomainTypeSeparator + 1);
    trackEvent(constants.events.DOMAIN_PURCHASE_CLICKED, {
      triggerId,
      domainName: searchString,
      domainNameSelected,
      domainTypeSelected,
      actionId,
      activeHostingSubscription: packageType,
    });
  };

  const handleSelect = ({ domain, searchString }) => {
    const actionId = actionIds.BUY_DOMAIN;

    trackDomainPurchaseClicked({ domain, searchString, actionId });

    const successUrl = shouldPublishOnSuccess ? getPublishUrl(domain) : null;

    onSelect({ domain, actionId, successUrl });
  };

  const onFreeSubdomainSelect = ({ searchString }) => {
    const domain = publishing.constants.FREE_YOLA_SUBDOMAIN;
    const successUrl = getPublishUrl(domain);
    const actionId = actionIds.GET_FREE_DOMAIN;

    trackDomainPurchaseClicked({
      domain,
      searchString,
      actionId,
    });

    onSelect({ domain, actionId, successUrl });
  };

  const onPurchasedDomainSelect = (domain) => {
    const successUrl = getPublishUrl(domain);
    const actionId = actionIds.EXISTING_DOMAIN;

    trackEvent(constants.events.DOMAIN_POINTING_CLICKED, {
      domainNameSelected: domain,
    });

    onSelect({ domain, actionId, successUrl });
  };

  const onConnectThirdPartyDomainClick = (domain) => {
    const myYolaDomainsUrl = myYolaConfig.domains_url;

    const searchParams = new URLSearchParams({
      third_party_domain: domain,
      site_id: siteId,
    });

    const domainsUrl = `${myYolaDomainsUrl}?${searchParams.toString()}`;

    onSelect({ domain, actionId: actionIds.THIRD_PARTY_DOMAIN, successUrl: domainsUrl });
  };

  const onTabChange = (tabId) => {
    trackEvent(constants.events.DOMAIN_SELECTOR_TABS_TRIGGER_CLICKED, {
      triggerId: tabId,
    });
  };

  let handleClose = null;

  if (onClose) {
    handleClose = () => {
      trackEvent(constants.events.DOMAIN_PURCHASE_DIALOG_CANCELED, {
        triggerId,
      });

      dispatch(dialogs.actions.hide());
      onClose();
    };
  }

  let handleNext = null;

  if (onNext) {
    handleNext = () => {
      trackEvent(constants.events.DOMAIN_PURCHASE_NOT_NOW_CLICKED, {
        triggerId,
      });

      onNext();
    };
  }

  const onDomainSearchFailure = () => {
    dispatch(actions.getDomainListFailure());

    trackEvent(constants.events.DOMAIN_SEARCH_FAILED, {
      triggerId,
    });
  };

  const onSearchStart = ({ searchString, domainsRequestOptions }) => {
    if (domainsRequestOptions && domainsRequestOptions.userAction) {
      trackEvent(constants.events.DOMAIN_PURCHASE_SEARCH_CLICKED, {
        triggerId,
        domainName: searchString,
      });
    }
  };

  const onDomainFieldChange = (option) => {
    if (option && option.tld) {
      trackEvent(constants.events.DOMAIN_PURCHASE_AUTOCOMPLETE_DOMAIN_SELECTED, {
        triggerId,
        domainName: option.value,
        tld: option.tld,
      });
    }
  };

  const onSearchPerformed = ({ searchString, searchResults, domainsRequestOptions }) => {
    const availableDomains = searchResults.filter(({ available }) => available);
    const isSearchedDomainAvailable = availableDomains.some(({ domain }) => {
      const domainName = domain.substring(0, domain.indexOf('.'));
      return searchString === domain || searchString === domainName;
    });
    const suggestedDomainsAvailable = availableDomains.length;
    const suggestedDomainsTaken = searchResults.length - availableDomains.length;

    trackEvent(constants.events.DOMAIN_PURCHASE_SEARCH_PERFORMED, {
      triggerId,
      domainName: searchString || null,
      isSearchedDomainAvailable,
      suggestedDomainsAvailable,
      suggestedDomainsTaken,
      searchClicked: Boolean(domainsRequestOptions && domainsRequestOptions.userAction),
    });
  };

  const domainName = useMemo(() => {
    if (domainNameSuggestion) {
      return domainNameSuggestion;
    }

    if (premiumFeature !== PUBLISHING) {
      return convertStringToDomainName(businessName || siteName);
    }

    const domainPart = businessName || siteName;
    const TLD_REGEXP = /(?<=\w\.)[a-zA-Z]{2,}$/;

    if (domainPart) {
      const tldMatch = domainPart.match(TLD_REGEXP);

      if (tldMatch) {
        const [tld] = tldMatch;
        const domainWithoutTld = domainPart.replace(TLD_REGEXP, '');

        return `${convertStringToDomainName(domainWithoutTld)}.${tld}`;
      }

      return `${convertStringToDomainName(domainPart)}.${DEFAULT_TLD}`;
    }

    return domainPart;
  }, [businessName, siteName, domainNameSuggestion, premiumFeature]);

  const businessInfo = useMemo(
    () => ({
      name: businessName,
      category: businessCategory,
      description: businessDescription,
    }),
    [businessName, businessCategory, businessDescription]
  );

  useEffect(() => {
    trackEvent(constants.events.DOMAIN_PURCHASE_DIALOG_DISPLAYED, {
      triggerId,
    });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <B2CDomainSelector
      currency={currency}
      appElementSelector={common.constants.common.APP_ELEMENT_SELECTOR}
      onSelect={handleSelect}
      onClose={handleClose}
      onNext={handleNext}
      onDomainFieldChange={onDomainFieldChange}
      domainName={domainName}
      onDomainSearchFailure={onDomainSearchFailure}
      onSearchStart={onSearchStart}
      onSearchPerformed={onSearchPerformed}
      siteId={siteId}
      withFreeSubdomain={withFreeSubdomain}
      onFreeSubdomainSelect={onFreeSubdomainSelect}
      purchasedDomainsData={purchasedDomainsData}
      captionsOverrides={captionsOverrides}
      isSpecialOfferAvailable={isSpecialOfferAvailable({
        extraPriceType,
        activeHostingSubscription,
      })}
      activeSubscriptionTerm={activeHostingSubscription?.term}
      isUpgradeToPublishEnabled={isUpgradeToPublishEnabled}
      isTrialDiscountAvailable={isTrialDiscountAvailable}
      extraPrice={extraPriceType}
      view={view}
      onPurchasedDomainSelect={onPurchasedDomainSelect}
      onTabChange={onTabChange}
      onConnectThirdPartyDomainClick={onConnectThirdPartyDomainClick}
      businessInfo={businessInfo}
      siteLocale={siteLocale}
      withBusinessInfo
    />
  );
}

B2cDomainPurchaseContainer.propTypes = {
  triggerId: PropTypes.string.isRequired,
  domainNameSuggestion: PropTypes.string,
  withFreeSubdomain: PropTypes.bool,
  withPurchasedDomains: PropTypes.bool,
  shouldPublishOnSuccess: PropTypes.bool,
  captionsOverrides: PropTypes.shape({}),
  premiumFeature: PropTypes.string,
  view: PropTypes.string,
  onClose: PropTypes.func,
  onNext: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
};

export default B2cDomainPurchaseContainer;
